<template>
  <v-container fluid>
    <v-card>
      <v-card-text class="pa-2 pa-sm-4">
        <v-row dense align="center" class="mb-2">
          <v-col cols="auto">
            <span class="text-h6">Лог событий</span>
          </v-col>
          <v-spacer />
          <v-col cols="auto">
            <v-btn icon @click="loadItems">
              <v-icon>mdi-reload</v-icon>
            </v-btn>
          </v-col>
        </v-row>

        <v-row dense>
          <template v-if="!userId && !controllerRoute">
            <v-col cols="12" sm="auto">
              <v-radio-group
                v-model="params.device"
                row
                dense
                hide-details
                class="mt-0"
              >
                <v-radio :value="null" label="Все устройства" />
                <v-radio value="controller" label="СКУД" />
                <v-radio value="kiosk" label="Терминал" />
              </v-radio-group>
            </v-col>
          </template>

          <v-col
            v-if="!userId || params.device === 'controller'"
            cols="12"
            sm="4"
            md="3"
          >
            <v-select
              v-model="params.device_id"
              :items="devices"
              item-text="label"
              item-value="value"
              label="Устройство"
              clearable
              dense
              hide-details
            />
          </v-col>

          <v-col cols="12" sm="4" md="2">
            <v-select
              v-model="params.type"
              :items="types"
              item-text="label"
              item-value="value"
              label="Тип события"
              clearable
              dense
              hide-details
            />
          </v-col>

          <v-col cols="12" sm="4" md="2">
            <v-menu
              ref="beginMenu"
              v-model="beginMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="beginDate"
                  label="Дата начала"
                  prepend-icon="mdi-calendar"
                  readonly
                  dense
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="clearBeginDate"
                />
              </template>
              <v-date-picker
                v-model="beginDate"
                no-title
                scrollable
                @input="beginMenu = false"
              />
            </v-menu>
          </v-col>

          <v-col cols="12" sm="4" md="2">
            <v-menu
              ref="endMenu"
              v-model="endMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              offset-y
              min-width="auto"
            >
              <template #activator="{ on, attrs }">
                <v-text-field
                  v-model="endDate"
                  label="Дата окончания"
                  prepend-icon="mdi-calendar"
                  readonly
                  dense
                  hide-details
                  clearable
                  v-bind="attrs"
                  v-on="on"
                  @click:clear="clearEndDate"
                />
              </template>
              <v-date-picker
                v-model="endDate"
                no-title
                scrollable
                @input="endMenu = false"
              />
            </v-menu>
          </v-col>
        </v-row>
      </v-card-text>

      <v-data-table
        :headers="headers"
        :items="items"
        :server-items-length="totalItems"
        :options.sync="tableOptions"
        :loading="loading"
        :footer-props="{
          'items-per-page-options': [10, 25, 50, 100]
        }"
        @click:row="onRowClick"
        @update:options="onTableOptionsChanged"
      >
        <template #[`item.date`]="{ item }">
          {{ formatDateTime(item.date) }}
        </template>

        <template #[`item.deviceId`]="{ item }">
          {{ getDeviceName(item.deviceId) }}
        </template>

        <template #[`item.user`]="{ item }">
          <template v-if="item.user">
            {{
              [item.user.surname, item.user.name, item.user.patronymic]
                .filter(Boolean)
                .join(" ")
            }}
          </template>
          <template v-else>
            —
          </template>
        </template>

        <template #[`item.type`]="{ item }">
          <v-chip
            :color="typeColor(item.type)"
            small
            dark
            label
            outlined
            style="min-width: 100pt"
          >
            {{ getTypeName(item.type) }}
          </v-chip>
        </template>

        <template #[`item.message`]="{ item }">
          <span style="white-space: pre-wrap; word-break: break-word">{{
            item.message
          }}</span>
        </template>
      </v-data-table>
    </v-card>
  </v-container>
</template>

<script>
import { groupBy, sortBy } from "lodash";
import { getEvents, getEventsByUserId } from "@/services/eventLog.service";
import { getControllers } from "@/services/device.service";
import { formatDateTime } from "@/utils/date-format";

export default {
  name: "EventLog",

  props: {
    userId: {
      type: [Number, String],
      default: null
    }
  },

  data() {
    const headers = [
      { text: "Дата", value: "date", width: 160 },
      { text: "Устройство", value: "deviceId", width: 200 },
      { text: "Тип", value: "type", width: 130 },
      { text: "Описание", value: "message" }
    ];

    if (!this.userId) {
      headers.splice(2, 0, {
        text: "Пользователь",
        value: "user",
        width: 200
      });
    }

    return {
      restoringQuery: false,
      loading: false,
      items: [],
      totalItems: 0,
      tableOptions: {
        page: 1,
        itemsPerPage: 25
      },

      params: {
        device: this.userId ? null : "controller",
        device_id: null,
        type: null,
        begin: null,
        end: null
      },

      beginDate: null,
      endDate: null,
      beginMenu: false,
      endMenu: false,

      devices: [],

      types: [
        { label: "Информация", value: "info" },
        { label: "Система", value: "system" },
        { label: "Успех", value: "success" },
        { label: "Предупреждение", value: "warn" },
        { label: "Ошибка", value: "error" }
      ],

      headers
    };
  },

  watch: {
    "params.device"(val) {
      if (val !== "controller") {
        this.params.device_id = null;
      }
      this.params.begin = this.beginDate;
      this.params.end = this.endDate;
      this.loadItems();
      this.syncToQuery();
    },

    "params.device_id"() {
      this.params.begin = this.beginDate;
      this.params.end = this.endDate;
      this.loadItems();
      this.syncToQuery();
    },

    "params.type"() {
      this.params.begin = this.beginDate;
      this.params.end = this.endDate;
      this.loadItems();
      this.syncToQuery();
    },

    beginDate(val) {
      this.params.begin = val;
      this.loadItems();
      this.syncToQuery();
    },

    endDate(val) {
      this.params.end = val;
      this.loadItems();
      this.syncToQuery();
    },

    userId() {
      this.loadItems();
    }
  },

  computed: {
    controllerRoute() {
      return this.$route.name === "CONTROLLERS_ROUTE";
    }
  },

  async created() {
    this.restoreFromQuery();
    await this.loadDevices();
    this.loadItems();
  },

  methods: {
    formatDateTime,

    restoreFromQuery() {
      const q = this.$route.query;
      if (Object.keys(q).some(k => k.startsWith("el_"))) {
        this.restoringQuery = true;
        if (q.el_device) this.params.device = q.el_device;
        if (q.el_deviceId) this.params.device_id = Number(q.el_deviceId);
        if (q.el_type) this.params.type = q.el_type;
        if (q.el_begin) {
          this.beginDate = q.el_begin;
          this.params.begin = q.el_begin;
        }
        if (q.el_end) {
          this.endDate = q.el_end;
          this.params.end = q.el_end;
        }
        this.tableOptions = {
          page: Number(q.el_page) || 1,
          itemsPerPage: Number(q.el_size) || 25
        };
        this.$nextTick(() => {
          this.restoringQuery = false;
        });
      }
    },

    syncToQuery() {
      if (this.restoringQuery) return;
      const q = { ...this.$route.query };
      q.el_device = this.params.device || undefined;
      q.el_deviceId = this.params.device_id || undefined;
      q.el_type = this.params.type || undefined;
      q.el_begin = this.beginDate || undefined;
      q.el_end = this.endDate || undefined;
      q.el_page = this.tableOptions.page || undefined;
      q.el_size = this.tableOptions.itemsPerPage || undefined;
      this.$router.replace({ query: q }).catch(() => {});
    },

    async loadDevices() {
      try {
        const controllers = await getControllers();
        const filtered = controllers.filter(c => c.type === 5);
        const grouped = groupBy(filtered, c => c.accessPoint?.name || "—");
        const result = [];
        const sortedGroups = sortBy(Object.keys(grouped)).reverse();
        for (const groupName of sortedGroups) {
          const sorted = sortBy(grouped[groupName], ["name"]);
          for (const c of sorted) {
            result.push({
              label: `${c.name}`,
              value: c.id
            });
          }
        }
        this.devices = result;
      } catch (e) {
        console.error("Failed to load controllers:", e);
      }
    },

    async loadItems() {
      this.loading = true;
      try {
        const queryParams = {
          page: (this.tableOptions.page || 1) - 1,
          size: this.tableOptions.itemsPerPage || 25,
          device: this.params.device || "any",
          type: this.params.type || "any"
        };

        if (this.params.device_id) {
          queryParams.device_id = this.params.device_id;
        }

        if (this.params.begin) {
          queryParams.begin = this.params.begin + " 00:00";
        }

        if (this.params.end) {
          queryParams.end = this.params.end + " 23:59";
        }

        const page = this.userId
          ? await getEventsByUserId(this.userId, queryParams)
          : await getEvents(queryParams);

        this.items = page.content;
        this.totalItems = page.totalElements;
      } catch (e) {
        console.error("Failed to load events:", e);
        this.items = [];
        this.totalItems = 0;
      } finally {
        this.loading = false;
      }
    },

    onRowClick(item) {
      if (this.userId == null && item.user && item.user.id) {
        let route_name;
        switch (item.user.mainGroup.name) {
          case "STAFF":
            route_name = "STAFF_EDIT_ROUTE";
            break;
          case "PPS":
            route_name = "PPS_EDIT_ROUTE";
            break;
          default:
            route_name = "USERS_EDIT_ROUTE";
        }
        const route = {
          name: route_name,
          params: { userId: String(item.user.id) },
          query: { tab: "eventLog" }
        };
        this.$router.push(route);
      }
    },

    onTableOptionsChanged() {
      this.loadItems();
      this.syncToQuery();
    },

    clearBeginDate() {
      this.beginDate = null;
      this.params.begin = null;
    },

    clearEndDate() {
      this.endDate = null;
      this.params.end = null;
    },

    getDeviceName(deviceId) {
      if (!deviceId) return "—";
      const device = this.devices.find(d => d.value === deviceId);
      return device ? device.label : `#${deviceId}`;
    },

    getTypeName(type) {
      const found = this.types.find(t => t.value === type);
      return found ? found.label : type || "—";
    },

    typeColor(type) {
      switch (type) {
        case "info":
          return "blue";
        case "system":
          return "grey darken-1";
        case "warn":
          return "orange";
        case "success":
          return "green";
        case "error":
          return "red";
        default:
          return "grey";
      }
    },

    formatDurationMs(ms) {
      if (ms == null) return "—";
      const totalSeconds = Math.floor(ms / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;

      const parts = [];
      if (hours > 0) parts.push(`${hours} ч`);
      if (minutes > 0) parts.push(`${minutes} мин`);
      if (seconds > 0 || parts.length === 0) parts.push(`${seconds} сек`);

      return parts.join(" ");
    }
  }
};
</script>
